/**
 * Visual effects: explosions, trails, engine flames.
 */

import * as THREE from 'three';
import type { Vec3, WeaponKey } from '../types';
import { buildExhaust, animateExhaust, PROJECTILE_SCALE, type ProjectileExhaust } from './projectileModels';
import { disposeObjectTree } from './meshBake';

// Reusable objects for orientation computation (avoid per-frame allocation)
const _lookMatrix = new THREE.Matrix4();
const _yUp = new THREE.Vector3(0, 1, 0);
const _xUp = new THREE.Vector3(1, 0, 0);
const _targetVec = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _q = new THREE.Quaternion();

/**
 * Orient a group so +Z follows `dir` with minimal roll (parallel transport):
 * the first call uses lookAt, later calls rotate by the shortest arc from
 * the previous forward vector. Avoids the roll snap of lookAt's up-vector
 * switching when the direction passes near vertical.
 */
export function orientAlongDirection(group: THREE.Object3D, dx: number, dy: number, dz: number): void {
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 0.001) return;
  _fwd.set(dx / len, dy / len, dz / len);
  const prev = group.userData.prevFwd as THREE.Vector3 | undefined;
  if (!prev) {
    const up = Math.abs(_fwd.y) > 0.99 ? _xUp : _yUp;
    _targetVec.copy(group.position).sub(_fwd);
    _lookMatrix.lookAt(group.position, _targetVec, up);
    group.quaternion.setFromRotationMatrix(_lookMatrix);
    group.userData.prevFwd = _fwd.clone();
    return;
  }
  if (prev.dot(_fwd) < 0.9999999) {
    _q.setFromUnitVectors(prev, _fwd);
    group.quaternion.premultiply(_q).normalize();
  }
  prev.copy(_fwd);
}

// =============================================================================
// ENGINE FLAME — per-weapon exhaust with animated cones and particles
// =============================================================================

export type EngineFlame = ProjectileExhaust;

export function createEngineFlame(scene: THREE.Scene, weaponKey: WeaponKey): EngineFlame {
  const exhaust = buildExhaust(weaponKey);
  exhaust.group.scale.setScalar(PROJECTILE_SCALE);
  scene.add(exhaust.group);
  return exhaust;
}

export function updateEngineFlame(
  flame: EngineFlame,
  position: Vec3,
  direction: Vec3,
  burning: boolean,
  dt: number,
  time: number
): void {
  flame.visible = burning;
  flame.group.visible = burning;

  if (!burning) return;

  // Position exhaust group at projectile position
  flame.group.position.set(position.x, position.y, position.z);

  // Orient so +Z (nose) points along velocity, roll-free
  orientAlongDirection(flame.group, direction.x, direction.y, direction.z);

  // Animate flame cones and particles
  animateExhaust(flame, dt, time);
}

export function disposeEngineFlame(scene: THREE.Scene, flame: EngineFlame): void {
  scene.remove(flame.group);
  disposeObjectTree(flame.group);
}

export interface Explosion {
  sphere: THREE.Mesh;
  particles: THREE.Mesh[];
  flash?: THREE.Mesh;         // additive core, visible from the impact frame
  life: number;
  maxLife: number;
  targetRadius: number;
  /** the profile's scale (1 = the giant class, today's sizes): the debris and the flash's floor shrink with the person */
  scale: number;
}

/**
 * Effect sizes are in world units from the giant class; at a person scale (k = 1/20) a fixed
 * 0.1 u debris lump is two people wide and a 0.3 u hit flash the size of a cottage. Every effect
 * with a fixed size takes `scale` (the profile's k; 1 keeps today exactly) — the blast radius
 * itself is the sim's and is not scaled here.
 */

/**
 * A fireball's drawn radius: the weapon's blast at classic, a person-scale burst under the person set — × min(1, `perK` × k),
 * so at k 1/20 a standard shell's 2-unit blast (36 people) draws ~2.7 people out and a heavy's ~4.4. Presentation only: the
 * blast still reaches its whole radius, and the crater and its decals show the footprint on the ground.
 */
export const FIREBALL = { perK: 1.5 } as const;
export function fireballRadius(blastRadius: number, k: number): number { return blastRadius * Math.min(1, FIREBALL.perK * k); }

export function createExplosion(
  scene: THREE.Scene,
  position: Vec3,
  radius: number,
  color: string,
  scale = 1,
): Explosion {
  const sphereGeo = new THREE.IcosahedronGeometry(1, 2);
  const sphereMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
    transparent: true,
    opacity: 0.6,
  });
  const sphere = new THREE.Mesh(sphereGeo, sphereMat);
  sphere.position.set(position.x, position.y, position.z);
  scene.add(sphere);

  const particles: THREE.Mesh[] = [];
  const particleGeo = new THREE.IcosahedronGeometry(0.1 * scale, 1);

  for (let i = 0; i < 20; i++) {
    const particleMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.7,
    });
    const p = new THREE.Mesh(particleGeo, particleMat);
    p.position.set(position.x, position.y, position.z);
    p.userData.vel = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 2
    )
      .normalize()
      .multiplyScalar(radius * (0.8 + Math.random() * 0.8));
    scene.add(p);
    particles.push(p);
  }

  // Flash core: bright, additive, already visible on the impact frame — the shell
  // starts small, so without it the projectile vanished into an empty frame (37.2d).
  const flash = new THREE.Mesh(
    new THREE.IcosahedronGeometry(1, 1),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(color).lerp(new THREE.Color('#ffffff'), 0.6), transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false }),
  );
  flash.position.set(position.x, position.y, position.z);
  scene.add(flash);
  const ex: Explosion = { sphere, particles, flash, life: 0, maxLife: 0.8, targetRadius: radius, scale };
  updateExplosion(ex, 0);   // frame-0 pose: shell a quarter out, flash full
  return ex;
}

export function updateExplosion(explosion: Explosion, dt: number): boolean {
  explosion.life += dt;
  const t = explosion.life / explosion.maxLife;

  if (t >= 1) return false;

  // Shell: fast ease-out growth from a quarter of the radius, fading as it thins
  const grow = 1 - Math.pow(1 - t, 3);
  const s = explosion.targetRadius * (0.25 + 0.75 * grow);
  explosion.sphere.scale.set(s, s, s);
  (explosion.sphere.material as THREE.MeshBasicMaterial).opacity = 0.55 * Math.pow(1 - t, 1.5);
  // Flash: swells to ~60% of the blast over the first 30%, then burns out
  if (explosion.flash) {
    const ft = Math.min(1, t / 0.3);
    const fs = Math.max(0.25 * explosion.scale, explosion.targetRadius * (0.25 + 0.35 * Math.sin(ft * Math.PI * 0.5)));
    explosion.flash.scale.setScalar(fs);
    (explosion.flash.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - ft);
    explosion.flash.visible = ft < 1;
  }

  for (const p of explosion.particles) {
    p.position.addScaledVector(p.userData.vel as THREE.Vector3, dt);
    (p.userData.vel as THREE.Vector3).multiplyScalar(0.96);
    (p.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - t);
  }

  return true;
}

export function disposeExplosion(scene: THREE.Scene, explosion: Explosion): void {
  scene.remove(explosion.sphere);
  explosion.sphere.geometry.dispose();
  (explosion.sphere.material as THREE.Material).dispose();
  if (explosion.flash) { scene.remove(explosion.flash); explosion.flash.geometry.dispose(); (explosion.flash.material as THREE.Material).dispose(); }

  for (const p of explosion.particles) {
    scene.remove(p);
    p.geometry.dispose();
    (p.material as THREE.Material).dispose();
  }
}

// =============================================================================
// ERUPTION EFFECT
// =============================================================================

export interface EruptionEffect {
  sphere: THREE.Mesh;
  debris: THREE.Mesh[];
  debrisVelocities: THREE.Vector3[];
  life: number;
  maxLife: number;
  position: Vec3;
}

export function createEruptionEffect(
  scene: THREE.Scene,
  position: Vec3,
  surfaceNormal: Vec3
): EruptionEffect {
  // Fire sphere
  const sphereGeo = new THREE.IcosahedronGeometry(0.5, 2);
  const sphereMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#d06030'),
    transparent: true,
    opacity: 0.7,
  });
  const sphere = new THREE.Mesh(sphereGeo, sphereMat);
  sphere.position.set(position.x, position.y, position.z);
  scene.add(sphere);

  // 8 cosmetic debris arcs
  const debris: THREE.Mesh[] = [];
  const debrisVelocities: THREE.Vector3[] = [];
  const debrisGeo = new THREE.IcosahedronGeometry(0.08, 0);

  const normal = new THREE.Vector3(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);

  for (let i = 0; i < 8; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(i % 2 === 0 ? '#e08040' : '#c05020'),
      transparent: true,
      opacity: 0.8,
    });
    const d = new THREE.Mesh(debrisGeo, mat);
    d.position.set(position.x, position.y, position.z);
    scene.add(d);
    debris.push(d);

    // Random velocity biased upward along surface normal
    const spread = new THREE.Vector3(
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4
    );
    const vel = normal.clone().multiplyScalar(3 + Math.random() * 4).add(spread);
    debrisVelocities.push(vel);
  }

  return {
    sphere,
    debris,
    debrisVelocities,
    life: 0,
    maxLife: 1.5,
    position,
  };
}

export function updateEruptionEffect(effect: EruptionEffect, dt: number, planets: Array<{ position: Vec3; mass: number }>): boolean {
  effect.life += dt;
  const t = effect.life / effect.maxLife;

  if (t >= 1) return false;

  // Expand and fade fire sphere
  const s = 0.5 + t * 1.5;
  effect.sphere.scale.set(s, s, s);
  (effect.sphere.material as THREE.MeshBasicMaterial).opacity = 0.7 * (1 - t);

  // Update debris with simple gravity
  for (let i = 0; i < effect.debris.length; i++) {
    const d = effect.debris[i];
    const vel = effect.debrisVelocities[i];

    // Simple gravity toward nearest planet center
    for (const planet of planets) {
      const dx = planet.position.x - d.position.x;
      const dy = planet.position.y - d.position.y;
      const dz = planet.position.z - d.position.z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq > 0.1) {
        const force = (planet.mass * 0.5) / distSq;
        const dist = Math.sqrt(distSq);
        vel.x += (dx / dist) * force * dt;
        vel.y += (dy / dist) * force * dt;
        vel.z += (dz / dist) * force * dt;
      }
    }

    d.position.x += vel.x * dt;
    d.position.y += vel.y * dt;
    d.position.z += vel.z * dt;

    (d.material as THREE.MeshBasicMaterial).opacity = 0.8 * (1 - t);
  }

  return true;
}

export function disposeEruptionEffect(scene: THREE.Scene, effect: EruptionEffect): void {
  scene.remove(effect.sphere);
  effect.sphere.geometry.dispose();
  (effect.sphere.material as THREE.Material).dispose();

  for (const d of effect.debris) {
    scene.remove(d);
    d.geometry.dispose();
    (d.material as THREE.Material).dispose();
  }
}

// =============================================================================
// SHATTER EFFECT (Crystal destruction)
// =============================================================================

export interface ShatterEffect {
  fragments: THREE.Mesh[];
  fragmentVelocities: THREE.Vector3[];
  life: number;
  maxLife: number;
}

export function createShatterEffect(
  scene: THREE.Scene,
  position: Vec3,
  scale = 1,
): ShatterEffect {
  const fragments: THREE.Mesh[] = [];
  const fragmentVelocities: THREE.Vector3[] = [];
  const fragGeo = new THREE.TetrahedronGeometry(0.08 * scale);
  const colors = [0x80a0c8, 0x7090b8, 0x90b0d8, 0xa0c0e0];

  for (let i = 0; i < 12; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: colors[i % colors.length],
      transparent: true,
      opacity: 0.8,
    });
    const frag = new THREE.Mesh(fragGeo, mat);
    frag.position.set(position.x, position.y, position.z);
    scene.add(frag);
    fragments.push(frag);

    const vel = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 2
    ).normalize().multiplyScalar((2 + Math.random() * 3) * scale);
    fragmentVelocities.push(vel);
  }

  return {
    fragments,
    fragmentVelocities,
    life: 0,
    maxLife: 1.0,
  };
}

export function updateShatterEffect(effect: ShatterEffect, dt: number, planets: Array<{ position: Vec3; mass: number }>): boolean {
  effect.life += dt;
  const t = effect.life / effect.maxLife;

  if (t >= 1) return false;

  for (let i = 0; i < effect.fragments.length; i++) {
    const frag = effect.fragments[i];
    const vel = effect.fragmentVelocities[i];

    // Simple gravity
    for (const planet of planets) {
      const dx = planet.position.x - frag.position.x;
      const dy = planet.position.y - frag.position.y;
      const dz = planet.position.z - frag.position.z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq > 0.1) {
        const force = (planet.mass * 0.3) / distSq;
        const dist = Math.sqrt(distSq);
        vel.x += (dx / dist) * force * dt;
        vel.y += (dy / dist) * force * dt;
        vel.z += (dz / dist) * force * dt;
      }
    }

    frag.position.x += vel.x * dt;
    frag.position.y += vel.y * dt;
    frag.position.z += vel.z * dt;

    // Tumble
    frag.rotation.x += dt * 5;
    frag.rotation.y += dt * 3;

    (frag.material as THREE.MeshBasicMaterial).opacity = 0.8 * (1 - t);
  }

  return true;
}

export function disposeShatterEffect(scene: THREE.Scene, effect: ShatterEffect): void {
  for (const frag of effect.fragments) {
    scene.remove(frag);
    frag.geometry.dispose();
    (frag.material as THREE.Material).dispose();
  }
}

// =============================================================================
// RIPPLE EFFECT (Sonora resonance)
// =============================================================================

export interface RippleEffect {
  ring: THREE.Mesh;
  life: number;
  maxLife: number;
}

export interface RippleOptions {
  color?: string;    // default '#c8c8e8' (geyser steam ring)
  size?: number;     // scale multiplier on the ring, default 1
  maxLife?: number;  // seconds, default 0.6
}
export function createRippleEffect(
  scene: THREE.Scene,
  position: Vec3,
  surfaceNormal: Vec3,
  opts: RippleOptions = {},
): RippleEffect {
  const ringGeo = new THREE.TorusGeometry(0.1 * (opts.size ?? 1), 0.03 * (opts.size ?? 1), 8, 24);
  const ringMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(opts.color ?? '#c8c8e8'),
    transparent: true,
    opacity: 0.6,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.position.set(position.x, position.y, position.z);

  // Orient ring perpendicular to surface normal
  const up = new THREE.Vector3(0, 0, 1); // torus default normal is Z
  const normal = new THREE.Vector3(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
  ring.quaternion.setFromUnitVectors(up, normal);

  scene.add(ring);

  return {
    ring,
    life: 0,
    maxLife: opts.maxLife ?? 0.6,
  };
}

export function updateRippleEffect(effect: RippleEffect, dt: number): boolean {
  effect.life += dt;
  const t = effect.life / effect.maxLife;

  if (t >= 1) return false;

  // Expand ring
  const s = 0.5 + t * 3.0;
  effect.ring.scale.set(s, s, s);
  (effect.ring.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - t);

  return true;
}

export function disposeRippleEffect(scene: THREE.Scene, effect: RippleEffect): void {
  scene.remove(effect.ring);
  effect.ring.geometry.dispose();
  (effect.ring.material as THREE.Material).dispose();
}

// =============================================================================
// IMPACT FADE — the projectile's last quarter second (37.2d)
// =============================================================================
/**
 * Takes ownership of the projectile mesh and its engine flame when the flight
 * ends, instead of deleting them on the impact frame (the hard cut).
 *   impact: the body slides the last bit into the impact point and shrinks into
 *           the explosion's flash over 0.25 s; the flame collapses with it.
 *   lost:   battery dead — the flame sputters out over 0.6 s while the body
 *           tumbles and fades over 0.9 s. No explosion.
 * Disposes both when done.
 */
export interface ImpactFade {
  mesh: THREE.Group | null;
  flame: EngineFlame | null;
  kind: 'impact' | 'lost';
  target: THREE.Vector3;      // impact point (impact) or last position (lost)
  start: THREE.Vector3;
  startScale: number;
  flameScale: number;
  life: number;
  maxLife: number;
}
export function createImpactFade(
  _scene: THREE.Scene,
  mesh: THREE.Group | null,
  flame: EngineFlame | null,
  point: Vec3,
  kind: 'impact' | 'lost',
): ImpactFade {
  const start = mesh ? mesh.position.clone() : new THREE.Vector3(point.x, point.y, point.z);
  const fade: ImpactFade = {
    mesh, flame, kind,
    target: new THREE.Vector3(point.x, point.y, point.z),
    start,
    startScale: mesh ? mesh.scale.x : 1,
    flameScale: flame ? flame.group.scale.x : 1,
    life: 0,
    maxLife: kind === 'impact' ? 0.25 : 0.9,
  };
  updateImpactFade(fade, 0);
  return fade;
}
const _fadeTmp = new THREE.Vector3();
export function updateImpactFade(fade: ImpactFade, dt: number): boolean {
  fade.life += dt;
  const t = Math.min(1, fade.life / fade.maxLife);
  if (fade.mesh) {
    if (fade.kind === 'impact') {
      // slide into the impact point over the first 40%, shrink with an ease-in
      const slide = Math.min(1, t / 0.4);
      fade.mesh.position.copy(_fadeTmp.copy(fade.start).lerp(fade.target, slide));
      fade.mesh.scale.setScalar(fade.startScale * Math.max(0, 1 - t * t));
    } else {
      // tumble and fade out
      fade.mesh.rotateX(dt * 1.8); fade.mesh.rotateZ(dt * 1.1);
      fade.mesh.scale.setScalar(fade.startScale * Math.max(0, 1 - Math.pow(t, 1.5)));
    }
  }
  if (fade.flame) {
    const g = fade.flame.group;
    if (fade.mesh) g.position.copy(fade.mesh.position);
    if (fade.kind === 'impact') {
      g.scale.setScalar(fade.flameScale * Math.max(0, 1 - t * 1.6));
    } else {
      // sputter: flicker for the first two thirds, then gone
      const ft = Math.min(1, t / 0.66);
      const flicker = 0.55 + 0.45 * Math.abs(Math.sin(fade.life * 31));
      g.scale.setScalar(fade.flameScale * Math.max(0, 1 - ft) * flicker);
    }
    (fade.flame.glow.material as THREE.MeshBasicMaterial).opacity *= 0.85;
    g.visible = g.scale.x > 1e-3;
  }
  return t < 1;
}
export function disposeImpactFade(scene: THREE.Scene, fade: ImpactFade): void {
  if (fade.mesh) { scene.remove(fade.mesh); disposeObjectTree(fade.mesh); fade.mesh = null; }
  if (fade.flame) { scene.remove(fade.flame.group); disposeObjectTree(fade.flame.group); fade.flame = null; }
}
